import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { AdministrationService } from '../../services/administration/administration.service';
import { GlobalDataService, SessionData } from '../../services/global-data/global-data.service';
import { ApplicationRole, editorStoreIds, hasRole, hasStoreContext, landingPath } from '../auth/roles';

type Permission = ApplicationRole | 'landing' | 'no-access' | 'store' | 'select-store' | 'sendouts' | 'sendout';

export function permissionRedirect(permission: Permission, session: SessionData): string | null {
  switch (permission) {
    case 'landing': return landingPath(session);
    case 'no-access': return landingPath(session) === 'no-access' ? null : landingPath(session);
    case 'store':
      return hasStoreContext(session) ? null : landingPath(session);
    case 'select-store': return editorStoreIds(session).length ? null : landingPath(session);
    case 'sendouts':
      if (hasStoreContext(session) && (session.storeImpersonation || !hasRole(session, 'SendoutManager'))) {
        return 'store-sendouts';
      }
      return hasRole(session, 'SendoutManager') ? null : landingPath(session);
    case 'sendout':
      return hasRole(session, 'SendoutManager') || hasStoreContext(session) ? null : landingPath(session);
    default:
      return hasRole(session, permission) ? null : landingPath(session);
  }
}

export function routePermission(route: ActivatedRouteSnapshot): Permission {
  if (route.data['permission'] === 'select-template') {
    return route.queryParamMap.get('multiStore') === 'true' ? 'SendoutManager' : 'store';
  }
  return route.data['permission'];
}

export const administrationGuard: CanActivateFn = route => {
  const router = inject(Router);
  const global = inject(GlobalDataService);
  const administration = inject(AdministrationService);
  const session = global.getSessionData().value;
  const generation = global.getAuthenticationGeneration();
  if (!session.initialized || !session.sessionVersion) return router.createUrlTree(['/']);

  return administration.getMyRoles().pipe(
    map(access => {
      if (!global.isAuthenticationCurrent(generation)) return false;
      const redirect = permissionRedirect(routePermission(route), { ...global.getSessionData().value, ...access });
      return redirect ? router.createUrlTree(['/', redirect]) : true;
    }),
    catchError(() => of(router.createUrlTree(['/access-unavailable'])))
  );
};
